import styles from './lotsStockTable.module.css'
import { FieldType, Column, Table, Section, Data } from "../../../../features/ui"
import { useLotLoaders, useProductLoaders } from '../../../products'
import { define } from '../../../../utils/typeUtils'
import { useStockLoaders } from '../../loaders/stockLoaders'
import { useEffect, useState } from 'react'
import { useStockHooks } from '../../hooks/stockHooks'

type Props = {
    productId: string
    branchId: string
    className?: string
}

export const LotsStockTable = (props: Props) => {
    const { productId, branchId, className } = props
    
    const [rows, setRows] = useState<Data[]>([])
    
    const { describeAvailableStock, describeReservedStock, describeTotalStock } = useStockHooks()

    const stockLoaders = useStockLoaders()
    const lotLoaders = useLotLoaders()
    const productLoaders = useProductLoaders()

    const loadRows = async () => {
        const { loadProduct } = productLoaders()
        const product = define(await loadProduct(productId))
        const { loadProductStocks } = stockLoaders()
        const productStocks = await loadProductStocks(productId, branchId)
        const lotIds = productStocks.map(stock => stock.lotId)
        const { loadLots } = lotLoaders()
        const lots = await loadLots(lotIds)
        
        const stocks = productStocks
            .filter(stock => productStocks.length === 1 || stock.availableUnits + stock.reservedUnits > 0)
            .sort((stock1, stock2) => {
                if (stock1.lotId && stock2.lotId) {
                    return stock1.lotId.localeCompare(stock2.lotId)
                } else {
                    return stock1.lotId ? -1 : 1
                }
            })

        return Promise.all(stocks.map(async stock => {
            const lot = define(lots.find(lot => lot.id === stock.lotId))
            return {
                lot: lot.code,
                availableUnits: await describeAvailableStock(product, branchId, lot.id),
                reservedUnits: await describeReservedStock(product, branchId, lot.id),
                totalUnits: await describeTotalStock(product, branchId, lot.id)
            }
        }))
    }

    useEffect(() => {
        loadRows().then(setRows)
    }, [productId, branchId])

    const detailColumns: Column[] = [
        {
            name: 'lot',
            type: FieldType.TEXT,
            label: 'Partida'
        },
        {
            name: 'availableUnits',
            type: FieldType.TEXT,
            label: 'Disponible'
        },
        {
            name: 'reservedUnits',
            type: FieldType.TEXT,
            label: 'Reservado'
        },
        {
            name: 'totalUnits',
            type: FieldType.TEXT,
            label: 'Total'
        }
    ]
    
    return (
        <Section
            id="stock-list-detail"
            space={12}
            className={`${className} ${styles.section}`}
            smallFont
        >
            <Table
                columns={detailColumns}
                rows={rows}
                rowId="lot"
                readOnly
            />
        </Section>
    )
}
